import {
	Accordion as ChakraAccordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	Flex,
	Text,
	AccordionIcon,
} from "@chakra-ui/react";
import { ReactElement, ReactNode, useState } from "react";

interface SingleAccordionProps {
	children?: ReactNode; // content inside the panel
	hideButton?: boolean;
	initialOpen?: boolean; // whether accordion starts open
	onAccordionChange?: (isOpen: boolean) => void;

	// Props overrides for default button/panel
	buttonProps?: React.ComponentProps<typeof AccordionButton>;
	panelProps?: React.ComponentProps<typeof AccordionPanel>;

	// Optionally override the entire button/panel
	renderButtonContent?: (isOpen: boolean) => ReactNode;
	renderPanelContent?: (
		close: () => void,
		open: () => void
	) => ReactElement<typeof AccordionPanel>;

	defaultButtonText?: string;
}

export interface SingleAccordionRef {
	open: () => void;
	close: () => void;
	toggle: () => void;
	isOpen: boolean;
}

export const SingleAccordion: React.FC<SingleAccordionProps> = ({
	children,
	hideButton = false,
	initialOpen = false,
	onAccordionChange,
	buttonProps,
	panelProps,
	renderButtonContent,
	renderPanelContent,
	defaultButtonText = "Click to expand",
}) => {
	const [isOpen, setIsOpen] = useState(initialOpen);

	const handleChange = () => {
		setIsOpenState(!isOpen);
	};

	const setIsOpenState = (newState: boolean) => {
		setIsOpen(newState);
		onAccordionChange?.(newState);
	};

	const close = () => setIsOpenState(false);
	const open = () => setIsOpenState(true);

	return (
		<ChakraAccordion
			allowToggle
			index={isOpen ? 0 : -1}
		>
			<AccordionItem border="none">
				{!hideButton && (
					<AccordionButton
						px={0}
						style={{ paddingLeft: "5px", borderRadius: "5px" }}
						_hover={{ bgColor: "#dbeafe" }}
						onClick={handleChange}
						{...buttonProps}
					>
						{renderButtonContent ? (
							renderButtonContent(isOpen)
						) : (
							<Flex
								w="full"
								justify="flex-start"
							>
								<Text className="text-gray-500">{defaultButtonText}</Text>
								<AccordionIcon />
							</Flex>
						)}
					</AccordionButton>
				)}
				<AccordionPanel
					px={0}
					pt={4}
					{...panelProps}
				>
					{renderPanelContent ? renderPanelContent(close, open) : children}
				</AccordionPanel>
			</AccordionItem>
		</ChakraAccordion>
	);
};
